import { Injectable } from '@angular/core';
import { AsyncSubject, Observable } from 'rxjs';
import { mergeMap, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { ConversationLegacy } from '../chat-inmessage/chat-legacy/model/message';
import { MessageService } from '../chat-inmessage/chat/services/message.service';
import { Conversation, Message, MessageTypeEnum } from '../chat-inmessage/chat/shared';
import { JWTService } from './jwt.service';
import { Page } from './page';
import { PageService } from './page.service';
import { SharedService } from './shared.service';

/**
 * This service orchestrates the calls to
 *  - GET /self (or /authenticate) to retreive a jwt and the page info
 *  - GET /conversation to retreive the N most recent exisisting messages for the current user
 *  - POST /conversation with a start-conversation message to start the bot (if a bot is defined)
 *  - window.parent.postMessage({ type: 'showFrame' }, '*') to have the iframe shown
 *
 * It answers the following questions:
 *  - when should we retreive the page info and the existing messages?
 *  - should we request the mbr to display the iframe at the end of app initialisation?
 *  - should we start a new conversation?
 *  - if yes, when should we start a new conversation?
 *
 *
 * The app can start in the following modes:
 *  - 'public' (embeded on a public facing website)
 *  - 'private' (embeded on a website where the user is authenticated)
 *  - 'link' (the user clicked on a linked in a an email or sms informing them of a new message)
 *  - 'standalone' (embedded in an Android or iOS app instead of an iframe)
 *
 *
 * Functional requirements:
 *
 * To avoid spamming our infrastructure, we do not create jwts when it is not necessary;
 * that is, in 'public' and 'private' mode, if we don't find an existing jwt,
 * we wait for the iframe to become visible before retreiving one with GET /self.
 *
 * In 'public' and 'private' mode, the iframe should stay hidden unless there are unseen messages in the conversation,
 * and if there are no messages in the conversation when the frame becomes visible (upon user click in this case),
 * then we should start a new conversation
 *
 * In 'link' mode, there will always be existing messages, we should always display the iframe upon startup,
 * and we should never start a new conversation
 *
 * In 'standalone' mode, the app will not be hidden during startup,
 * and we should start a new conversation if there are no existing messages for the user
 *
 *
 * Implementation:
 *
 *  - 'public' and 'private' modes
 *    - on init:
 *      - if existing inmessage jwt in localstorage:
 *        -> get page info and existing messages
 *        -> ask mbr to show iframe if unseen messages
 *      - else nothing
 *    - when frame becomes visible:
 *      -> wait until any actions started at init have completed, to avoid race conditions
 *      -> get page info and existing messages if not done at init (that is, if no existing inmessage jwt in localstorage)
 *      -> start new conversation if no existing messages
 *
 *  - 'link' mode:
 *    - on init:
 *      -> get page info and existing messages
 *      -> ask mbr to show iframe
 *    - when frame becomes visible: nothing
 *
 *  - 'standalone' mode:
 *    - on init:
 *      -> get page info and existing messages
 *      -> start new conversation if no existing messages
 *    - when frame becomes visible: N/A
 *
 */

export enum StartupMode {
  Public = 'public',
  Private = 'private',
  Link = 'link',
  Standalone = 'standalone',
}

@Injectable()
export class AppStartupService {
  pageInfo$ = new AsyncSubject<Page>();
  initialConversation$ = new AsyncSubject<Conversation | ConversationLegacy>();

  private initCompleted$ = new AsyncSubject<void>();
  private hasBecomeVisibleOnce = false;

  startupMode: StartupMode;

  constructor(
    private pageService: PageService,
    private messageService: MessageService,
    private jwtService: JWTService,
    private translateService: TranslateService,
    private httpClient: HttpClient,
    private sharedService: SharedService
  ) {
    this.setStartupMode();

    window.addEventListener('message', event => {
      if (event.data.type === 'becomesVisible') {
        this.iframeBecomesVisible(event.data.ref);
      }
    });
  }

  private setStartupMode() {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('standalone') === 'true') {
      this.startupMode = StartupMode.Standalone;
    } else if (urlParams.get('x-inmessage-jwt')) {
      // inMessage jwt passed via the host page URL after clicking a link in an email or sms
      this.startupMode = StartupMode.Link;
    } else if (urlParams.get('jwt')) {
      // 3rd party jwt passed by the host page so that they can match the message sender in Alcmeon with the user in their IT system
      this.startupMode = StartupMode.Private;
    } else {
      this.startupMode = StartupMode.Public;
    }
  }

  initApp() {
    if (this.startupMode === StartupMode.Standalone) {
      this.initStandaloneMode();
    } else if (this.startupMode === StartupMode.Link) {
      this.initLinkMode();
    } else {
      this.initPublicOrPrivateMode();
    }
  }

  getLabels(langCode?: string) {
    return this.httpClient
      .get<
        Record<string, string>
      >(`${environment.apiURLPrefix}${this.pageService.getPageId()}/labels`, { params: { lang_code: langCode } })
      .subscribe({
        next: labels => {
          if (labels) {
            const { helperMessage1, helperMessage2, helperMessage3, helperMessage4 } = labels;
            window.parent.postMessage(
              {
                type: 'receivedTranslations',
                labels: { helperMessage1, helperMessage2, helperMessage3, helperMessage4 },
              },
              '*'
            );
            for (const [key, value] of Object.entries(labels)) {
              this.translateService.set(key, value, this.translateService.currentLang);
            }
          }
        },
      });
  }

  private initPublicOrPrivateMode() {
    if (this.jwtService.hasJwt()) {
      this.getPageInfoAndInitialConversation().subscribe(conversation => {
        this.showIFrameIfUnseenMessages(conversation);
        this.initCompleted();
      });
    } else {
      this.initCompleted();
    }
  }

  private initLinkMode() {
    this.getPageInfoAndInitialConversation().subscribe(() => {
      this.showIFrame();
      this.initCompleted();
    });
  }

  private initStandaloneMode() {
    this.getPageInfoAndInitialConversation().subscribe(conversation => {
      this.startNewConversationIfNeeded(conversation);
      this.initCompleted();
    });
  }

  /**
   * @param ref comes from mbr js, in showFrameAndButtons(). It's used by our clients to trigger a box on demand.
   */
  private iframeBecomesVisible(ref?) {
    if (!this.hasBecomeVisibleOnce) {
      this.hasBecomeVisibleOnce = true;
      // wait for all init activity to be completed, to avoid race conditions
      this.initCompleted$.subscribe(() => {
        if (this.startupMode === StartupMode.Public || this.startupMode === StartupMode.Private) {
          if (!this.jwtService.hasJwt()) {
            this.getPageInfoAndInitialConversation().subscribe();
          }
          this.initialConversation$.subscribe(conversation => this.startNewConversationIfNeeded(conversation, ref));
        }
        // nothing to do for other modes
      });
    } else if (ref) {
      this.startNewConversationIfNeeded(null, ref);
    }
  }

  private getPageInfoAndInitialConversation(): Observable<Conversation> {
    return this.pageService.getPageInfo().pipe(
      tap(pageInfo => this.emitPageInfo(pageInfo)),
      mergeMap(() => this.messageService.getMostRecentMessages()),
      tap(conversation => this.emitInitialConversation(conversation))
    );
  }

  private showIFrameIfUnseenMessages(conversation) {
    const messages = conversation?.messages || [];
    const messagesFromUser = messages.filter(
      message => message.is_from_brand === false && message.type !== MessageTypeEnum.START_CONVERSATION
    );
    const hasMessageFromUser = messagesFromUser.length > 0;
    const hasUnseenMessage =
      messages.length > 0 && messages[messages.length - 1].id !== conversation?.last_seen_message_id;

    if (hasMessageFromUser && hasUnseenMessage) {
      this.showIFrame();
    }
  }

  private showIFrame() {
    window.parent.postMessage({ type: 'showFrame' }, '*');
  }

  private startNewConversationIfNeeded(conversation, ref?) {
    const urlParams = new URLSearchParams(window.location.search);
    if (ref) {
      this.messageService.postMessage(Message.newStartConversationMessage(ref)).subscribe();
    } else if (urlParams.get('ref')) {
      this.messageService.postMessage(Message.newStartConversationMessage(urlParams.get('ref'))).subscribe();
    } else if (!conversation || !conversation.messages || !conversation.messages.length) {
      this.messageService.postMessage(Message.newStartConversationMessage()).subscribe();
    }
  }

  private initCompleted() {
    this.initCompleted$.next(null);
    this.initCompleted$.complete();
  }

  private emitPageInfo(pageInfo: Page) {
    this.sharedService.page = pageInfo;
    this.pageInfo$.next(pageInfo);
    this.pageInfo$.complete();
    if (/^https:\/\/(admin-dev[\d|a-z]\.)?(demo\.)?alcmeon.com/.test(document.referrer)) {
      this.pageService.fakeBackgroundImageBS.subscribe(base64 => {
        window.parent.postMessage({ type: 'changeBackground', url: base64 }, '*');
      });
    }
  }

  private emitInitialConversation(conversation: Conversation | ConversationLegacy) {
    this.initialConversation$.next(conversation);
    this.initialConversation$.complete();
  }

  public isStandaloneMode() {
    return this.startupMode === StartupMode.Standalone;
  }
}
